import {
  INFORMA_TARGET_ENTITY,
  INFORMA_BOOKMARK_ACTION,
  InformaMfeUserActionContext,
  InformaMfeUserContext,
  InformaMfeMatchContext,
  InformaMfeNavigateValue,
  BookmarkData,
  UserActionPayload,
} from '@base/models/others/informa-mfe-event';
import { TypeOfRequestLabel, TypeOfOperation } from '@base/enums/request-label.enum';
import { StatusResponse } from '@base/enums/status-response.enum';

const HOST_TO_INFORMA_LANGUAGE_MAP = {
  en: 'en',
  cn: 'zh-Hans',
  sc: 'zh-Hans',
  'zh-cn': 'zh-Hans',
  tc: 'tc-HAZ',
  'zh-tw': 'tc-HAZ',
} as const;

export function mapHostLanguageToInforma(hostLang: string | null | undefined): string {
  const normalizedHostLanguage = hostLang?.trim().toLowerCase();

  if (!normalizedHostLanguage) {
    return 'en';
  }

  return HOST_TO_INFORMA_LANGUAGE_MAP[normalizedHostLanguage as keyof typeof HOST_TO_INFORMA_LANGUAGE_MAP] ?? 'en';
}

// ─── Navigate entity resolution ───────────────────────────────────────────

/**
 * Resolves the translate-entity string for a NAVIGATE event.
 *
 * For `MEETING_REQUEST` the payload carries `entityType` (e.g. `'EXHIBITOR'`)
 * identifying what kind of entity the meeting is about — that takes priority
 * over the target-level fallback so `translateExternalId` uses the right type.
 */
export function resolveTranslateEntity(nav: InformaMfeNavigateValue): string | undefined {
  if (nav.target === 'MEETING_REQUEST' && nav.entityType) {
    return (
      INFORMA_TARGET_ENTITY[nav.entityType as InformaMfeNavigateValue['target']] ?? INFORMA_TARGET_ENTITY[nav.target]
    );
  }
  return INFORMA_TARGET_ENTITY[nav.target];
}

// ─── user-context ─────────────────────────────────────────────────────────

/**
 * Builds the JSON string for the `user-context` attribute consumed by all Informa MFEs.
 * `meetingSfIds` and `recommendedSfIds` are not yet sourced from Jublia — empty for now.
 *
 * @param localBookmarks - Set of Informa SF IDs bookmarked in this session.
 */
export function buildUserContext(localBookmarks: Set<string>): string {
  const ctx: InformaMfeUserContext = {
    bookmarkedSfIds: [...localBookmarks],
    meetingSfIds: [],
    recommendedSfIds: [],
  };
  return JSON.stringify(ctx);
}

// ─── Pure helpers ──────────────────────────────────────────────────────────

/** Returns whether the current page group is enabled for an Informa MFE. */
export function isInformaGroupMember(
  groupIds: readonly string[] | null | undefined,
  pgId: string | null | undefined
): boolean {
  return Boolean(pgId && (groupIds ?? []).includes(pgId));
}

/** Extracts display-ready match copy from a NAVIGATE context without rewriting it. */
export function resolveInformaMatchContext(context: unknown): InformaMfeMatchContext | null {
  if (typeof context !== 'object' || context === null || Array.isArray(context)) return null;

  const rawContext = context as Record<string, unknown>;
  const matchReason = typeof rawContext['matchReason'] === 'string' ? rawContext['matchReason'] : undefined;
  const rawMatchReasons = rawContext['matchReasons'];
  const matchReasons =
    Array.isArray(rawMatchReasons) && rawMatchReasons.every((reason) => typeof reason === 'string')
      ? rawMatchReasons
      : undefined;
  const hasMatchReason = Boolean(matchReason);
  const hasMatchReasons = matchReasons?.some(Boolean) ?? false;

  if (!hasMatchReason && !hasMatchReasons) return null;

  return {
    ...(hasMatchReason ? { matchReason } : {}),
    ...(hasMatchReasons && matchReasons ? { matchReasons: [...matchReasons] } : {}),
  };
}

/**
 * Validates and extracts fields from a raw `USER_ACTION` event value.
 * Returns `null` if any required field is missing.
 *
 * @param raw - Raw `value` from the MFE event.
 */
export function parseUserActionPayload(raw: Record<string, unknown>): UserActionPayload | null {
  const action = raw['action'] as string;
  if (!action) return null;
  // Floorplan emits { action, sfAccountId } — always an exhibitor/company booth
  if (raw['sfAccountId']) {
    return { action, entityType: 'EXHIBITOR', entityId: raw['sfAccountId'] as string };
  }
  const entityType = raw['entityType'] as string;
  const entityId = raw['entityId'] as string;
  if (!entityId) return null;
  const saved = typeof raw['saved'] === 'boolean' ? raw['saved'] : undefined;
  // Chat emits { action, entityId } without entityType — default to EXHIBITOR
  return { action, entityType: entityType || 'EXHIBITOR', entityId, saved };
}

/**
 * Resolves the translate-entity string and Jublia bookmark label for a given
 * Informa `entityType`. Returns `null` when the type is unsupported.
 *
 * @param entityType - Informa entity type string (e.g. `'EXHIBITOR'`).
 */
export function resolveBookmarkMeta(
  entityType: string
): { translateEntity: string; bookmarkAction: TypeOfRequestLabel } | null {
  const translateEntity = INFORMA_TARGET_ENTITY[entityType as keyof typeof INFORMA_TARGET_ENTITY];
  const bookmarkAction = INFORMA_BOOKMARK_ACTION[entityType];
  if (!translateEntity || !bookmarkAction) return null;
  return { translateEntity, bookmarkAction };
}

/**
 * Determines the bookmark operation for `entityId` and mutates `localBookmarks`.
 *
 * - Not in set → `POSITIVE` (add bookmark).
 * - In set → `NEGATIVE` (remove bookmark).
 *
 * @param entityId - Informa external entity identifier.
 * @param localBookmarks - Mutable set of currently bookmarked Informa IDs.
 */
export function toggleBookmarkOperation(entityId: string, localBookmarks: Set<string>): TypeOfOperation {
  if (localBookmarks.has(entityId)) {
    localBookmarks.delete(entityId);
    return TypeOfOperation.NEGATIVE;
  }
  localBookmarks.add(entityId);
  return TypeOfOperation.POSITIVE;
}

// ─── Side-effect helpers ───────────────────────────────────────────────────

/**
 * Translates `entityId` to a Jublia hash then calls `RequestLabelService.updateLabel`.
 * Resolves on success, rejects on any API or translation error — callers decide whether to log.
 *
 * @param data - {@link BookmarkData} — what to save.
 * @param ctx - {@link InformaMfeUserActionContext} — services and log prefix.
 */
export async function persistBookmark(data: BookmarkData, ctx: InformaMfeUserActionContext): Promise<void> {
  const { translateEntity, entityId, bookmarkAction, operation } = data;
  const { externalEventSrv, requestLabelSrv } = ctx;
  const persist = (id: string): Promise<void> =>
    new Promise((resolve, reject) =>
      requestLabelSrv.updateLabel({ id_object: id, action: bookmarkAction, operation }).subscribe({
        next: (res) => {
          if (res.status === StatusResponse.TEXT_SUCCESS) resolve();
          else reject(new Error(`Bookmark persist rejected with status: ${res.status}`));
        },
        error: reject,
      })
    );
  const hash = await externalEventSrv.translateExternalId(translateEntity, entityId);
  await persist(hash);
}

// ─── Action handlers ───────────────────────────────────────────────────────

/**
 * Handles the `bookmark_toggle` action: resolves bookmark metadata, calls the API,
 * then — only on success — mutates `localBookmarks`.
 *
 * When `saved` is provided (flat payload), it is used as the authoritative
 * target state. When absent (legacy envelope), state is inferred by toggling.
 * Rejects if the API call fails so the caller can skip the `user-context` update.
 */
async function handleBookmarkToggle(
  entityType: string,
  entityId: string,
  ctx: InformaMfeUserActionContext,
  saved?: boolean
): Promise<void> {
  const meta = resolveBookmarkMeta(entityType);
  if (!meta) {
    console.warn(`${ctx.logPrefix}: unsupported bookmark entityType: ${entityType}`);
    return;
  }
  // Determine desired operation without mutating localBookmarks yet
  let operation: TypeOfOperation;
  if (saved !== undefined) {
    operation = saved ? TypeOfOperation.POSITIVE : TypeOfOperation.NEGATIVE;
  } else {
    operation = ctx.localBookmarks.has(entityId) ? TypeOfOperation.NEGATIVE : TypeOfOperation.POSITIVE;
  }

  await persistBookmark(
    { translateEntity: meta.translateEntity, entityId, bookmarkAction: meta.bookmarkAction, operation },
    ctx
  );

  // Mutate only after API success
  if (operation === TypeOfOperation.POSITIVE) ctx.localBookmarks.add(entityId);
  else ctx.localBookmarks.delete(entityId);
}

// ─── Entry point ───────────────────────────────────────────────────────────

/**
 * Dispatches `USER_ACTION` events from any Informa MFE host to the appropriate
 * action handler. Unknown or unparseable payloads are logged and ignored.
 *
 * Add new `case` entries here as the MFE introduces additional action types.
 *
 * @param payload - Raw `value` from the MFE `USER_ACTION` event.
 * @param ctx - {@link InformaMfeUserActionContext}
 */
export async function handleInformaMfeUserAction(
  payload: Record<string, unknown>,
  ctx: InformaMfeUserActionContext
): Promise<void> {
  const parsed = parseUserActionPayload(payload);
  if (!parsed) {
    console.info(`${ctx.logPrefix} user action:`, payload);
    return;
  }
  switch (parsed.action) {
    case 'bookmark_toggle':
      await handleBookmarkToggle(parsed.entityType, parsed.entityId, ctx, parsed.saved);
      break;
    default:
      console.info(`${ctx.logPrefix} user action:`, payload);
  }
}
